import './App.css';
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import {Button, Nav, NavDropdown, Badge} from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import ManageJob from "./components/ManageJob";
import ManageUser from "./components/ManageUser";
import Scheduler from "./components/Scheduler";
import UserDetails from "./components/UserDetails";
import AddUser from "./components/AddUser";
import GuestyIntegration from "./components/GuestyIntegration";
import GuestyMessageHistory from "./components/GuestyMessageHistory";
import Login from "./components/Login";
import {isValidLogin} from "./service/authService";
import UpdateJob from "./components/UpdateJob";
import ReportLostProperties from "./components/ReportLostProperties";
import {useEffect, useState} from "react";
import {jwtDecode} from "jwt-decode";
import ManageProperty from "./components/ManageProperty";
import JobSupervising from "./components/JobSupervising";
import {getGuestyFailedMessagesCount} from "./service/UserService";
import AccountantPage from "./components/accountant/AccountantPage";
import {UI_VERSION} from "./lib/uiVersion";

// Define the router configuration
const router = createBrowserRouter([
    {
        path: "/",
        element: <Scheduler />
    },
    {
        path: "/login",
        element: <Login />
    },
    {
        path: "/scheduler",
        element: <Scheduler />
    },
    {
        path: "/job-supervising",
        element: <JobSupervising />
    },
    {
        path: "/manage-job",
        element: <ManageJob />
    },
    {
        path: "/manage-job/update-job/:cos_job_id",
        element: <UpdateJob />
    },
    {
        path: "/manage-user",
        element: <ManageUser />
    },
    {
        path: "/report-lost-properties",
        element: <ReportLostProperties />
    },
    {
        path: "/manage-user/user-details/:username",
        element: <UserDetails />
    },
    {
        path: "/manage-user/add-user",
        element: <AddUser />
    },
    {
        path: "/manage-user/guesty-integration/:cosCustomerId",
        element: <GuestyIntegration />
    },
    {
        path: "/manage-property",
        element: <ManageProperty />
    },
    {
        path: "/guesty-messages",
        element: <GuestyMessageHistory />
    },
    {
        path: "/accountant",
        element: <AccountantPage />
    }
]);

function App(props) {
    const [failedMessagesCount, setFailedMessagesCount] = useState(0);

    useEffect(() => {
        let currentUser = getCurrentUserInfo()
        if (currentUser !== undefined) {
            window.newrelic?.setUserId(currentUser.cosCustomerName)
        }

        // Fetch failed messages count if user is admin
        if (isValidLogin('ADMIN')) {
            fetchFailedMessagesCount();
            
            // Set up periodic refresh every 5 minutes
            const intervalId = setInterval(() => {
                fetchFailedMessagesCount();
            }, 5 * 60 * 1000);
            
            // Listen for count updates from GuestyMessageHistory component
            const handleCountUpdate = (event) => {
                if (event.detail && event.detail.count !== undefined) {
                    setFailedMessagesCount(event.detail.count);
                }
            };
            
            window.addEventListener('failedMessagesCountUpdated', handleCountUpdate);
            
            // Clean up interval and event listener on component unmount
            return () => {
                clearInterval(intervalId);
                window.removeEventListener('failedMessagesCountUpdated', handleCountUpdate);
            };
        }
    }, []);

    // Function to fetch failed messages count
    const fetchFailedMessagesCount = () => {
        getGuestyFailedMessagesCount()
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => {
                        if (result && result.count !== undefined) {
                            setFailedMessagesCount(result.count);
                        }
                    });
                }
            })
            .catch(error => {
                console.error("Error fetching failed messages count:", error);
            });
    };

    function getCurrentUserInfo() {
        let cosToken = localStorage.getItem('cosToken')
        if (cosToken == null) {
            return {}
        }
        let decodedToken = jwtDecode(cosToken);

        return decodedToken?.optinestUser?.optinestApiUser
    }

    return (
        <div className="App">

            {isValidLogin('ADMIN') ? <>
                    <Nav defaultActiveKey="/scheduler" as="ul">
                        <Nav.Item as="li">
                            <img width={"100px"} height={"38px"} style={{marginLeft: "10px"}}
                                 src={"/" + process.env.PUBLIC_URL + "UrbanAideBanner.svg"} alt={"UrbanAide Logo"}/>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Nav.Link href="/scheduler">AirBnB Scheduler</Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Nav.Link href="/job-supervising">Supervising</Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Nav.Link href="/manage-job">Job Management</Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Nav.Link href="/manage-user">User Management</Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Nav.Link href="/manage-property">Property Management</Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Nav.Link href="/accountant">Accountant</Nav.Link>
                        </Nav.Item>
                        <NavDropdown title="Report" id="nav-dropdown">
                            <NavDropdown.Item href="/report-lost-properties">Lost Properties</NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown 
                            title={
                                <span style={{ position: 'relative', paddingRight: '8px' }}>
                                    Integration
                                    {failedMessagesCount > 0 && (
                                        <Badge 
                                            bg="danger" 
                                            pill 
                                            style={{
                                                position: 'absolute',
                                                top: '-10px',
                                                right: '-10px',
                                                fontSize: '10px',
                                                minWidth: '18px',
                                                height: '18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 0 0 2px white',
                                                backgroundColor: '#dc3545' // Explicit red color
                                            }}
                                        >
                                            {failedMessagesCount}
                                        </Badge>
                                    )}
                                </span>
                            } 
                            id="integration-dropdown"
                        >
                            <NavDropdown.Item href="/guesty-messages">
                                Guesty Messages
                                {failedMessagesCount > 0 && (
                                    <Badge 
                                        bg="danger" 
                                        pill 
                                        className="ms-2"
                                        style={{
                                            backgroundColor: '#dc3545' // Explicit red color
                                        }}
                                    >
                                        {failedMessagesCount}
                                    </Badge>
                                )}
                            </NavDropdown.Item>
                        </NavDropdown>
                        <Nav.Item as="li">
                            <span className="text-muted small d-inline-block"
                                  style={{
                                      height: "36px",
                                      lineHeight: "36px",
                                      margin: "4px 0"
                                  }}
                            >{UI_VERSION}</span>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Button variant="secondary" size={"sm"}
                                    style={{
                                        height: "36px",
                                        margin: "4px"
                                    }}
                                    onClick={() => {
                                        localStorage.removeItem("cosToken");
                                        window.location.href = "/login"
                                    }
                                    }
                            >Logout</Button>
                        </Nav.Item>
                    </Nav>
                    <hr style={{marginTop: "0px"}}/>
                    <RouterProvider router={router} />

                </>
                : <Login/>}


        </div>);
}

export default App;
